
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { LogOut, Mail, Shield } from "lucide-react";
import { useStore } from "@/store/useStore";

interface OTPVerificationModalProps {
    open: boolean;
    email?: string;
    onVerify?: (otp: string) => void;
    onResend?: () => void;
}

export function OTPVerificationModal({
    open,
    email = "your email",
    onVerify,
    onResend
}: OTPVerificationModalProps) {
    const [otp, setOtp] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const updateUserData = useStore((state) => state.updateUserData);
    const logout = useStore((state) => state.logout);

    const handleVerify = async () => {
        if (otp.length !== 6) return;

        setIsLoading(true);
        try {
            await onVerify?.(otp);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = () => {
        setIsLoading(true);
        setOtp("");
        onResend?.();
        setIsLoading(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, "").slice(0, 6);
        setOtp(value);
    };

    return (
        <Dialog open={open}>
            <DialogContent
                className="w-full max-w-none h-screen flex flex-col items-center justify-center gap-8 bg-background/70 border-0 rounded-none"
            >
                <DialogTitle className="mb-4">
                    <div className="flex flex-col items-center space-y-4">
                        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                            <Shield className="w-12 h-12 text-primary" />
                        </div>

                        <div className="text-center space-y-2">
                            <h1 className="text-3xl font-bold text-foreground">Complete Your Sign-Up</h1>
                            <div className="text-muted-foreground text-lg justify-center item-center flex gap-2">
                                <span>Verify your email to continue or </span>
                                <div onClick={logout} className="text-red-500 cursor-pointer hover:text-red-700 text-sm flex gap-1 items-center">
                                    <LogOut className="w-4 h-4" />
                                    Logout
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogTitle>

                <div className="w-full max-w-md space-y-6">
                    <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
                        <Mail className="w-4 h-4" />
                        <span>We sent a code to {email}</span>
                    </div>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <label htmlFor="otp" className="text-sm font-medium text-foreground">
                                Enter verification code
                            </label>
                            <Input
                                id="otp"
                                type="text"
                                placeholder="000000"
                                value={otp}
                                onChange={handleInputChange}
                                className="text-center text-2xl border border-gray-300 hover:border-secondary tracking-widest font-mono h-14"
                                maxLength={6}
                            />
                        </div>

                        <Button
                            onClick={handleVerify}
                            className="w-full h-12 text-lg bg-secondary hover:bg-secondary/80"
                            disabled={otp.length !== 6 || isLoading}
                        >
                            {isLoading ? "Verifying..." : "Verify Email"}
                        </Button>
                    </div>

                    <div className="text-center space-y-2">
                        <p className="text-sm text-muted-foreground">
                            Didn't receive the code?
                        </p>
                        <Button
                            variant="ghost"
                            onClick={handleResend}
                            className="text-sm text-primary bg-purple-800 hover:text-primary/80"
                        >
                            {isLoading && !otp ? "Sending code..." : "Resend verification code"}
                        </Button>
                        <div className="text-center text-sm text-muted-foreground">
                            Already verified?{" "}
                            <a onClick={() => updateUserData()} className="cursor-pointer text-red-700 underline hover:text-red-700/80">
                                Proceed to the app
                            </a>
                        </div>
                    </div>
                </div>

                <div className="text-center text-xs text-muted-foreground max-w-md">
                    Check your spam folder if you don't see the email. The code expires in 10 minutes.
                </div>
            </DialogContent>
        </Dialog>
    );
}